import type { ProductProps } from '@/types';

/**
 * Cache compartilhado dos produtos já abertos do IndexedDB e precificados.
 *
 * Depois que um produto foi montado (linha do catálogo + preço + última compra), reabri-lo em
 * outra tela é trabalho repetido: a lista, o carrinho e a tela de produto pedem os mesmos
 * códigos. Este store guarda o resultado e é a fonte única para todos eles.
 *
 * Duas coisas o mantêm honesto com a memória:
 *
 *  - **teto com descarte FIFO**: rolar o catálogo inteiro não pode recriar o dicionário completo
 *    que a Fase 2 veio remover. O que passa do teto é descartado por ordem de entrada;
 *  - **fixados**: os itens do carrinho e do checkout não podem ser descartados, senão a tela
 *    perderia o produto no meio de um cálculo e ninguém pediria de volta.
 *
 * O store é módulo, não contexto: quem consome está espalhado em provedores diferentes, e um
 * contexto novo seria mais um nível na árvore só para carregar dado.
 */

/** ~600 produtos ≈ 2 MB. Acima disso já não é cache, é o dicionário inteiro de volta. */
const MAX_PRODUTOS = 600;

const store = new Map<number, ProductProps>();
/** Códigos que não podem ser descartados (itens de carrinho/checkout em uso). */
const pinned = new Set<number>();
/**
 * Códigos já abertos que não viraram produto — restritos para o cliente ou sem embalagem no
 * escopo. Sem isso, a lista tentaria reabrir esses códigos a cada página.
 */
const misses = new Set<number>();

let snapshot: Record<number, ProductProps> = {};
let scopeSignature = '';
const listeners = new Set<() => void>();

const emit = () => {
  for (const listener of listeners) listener();
};

/**
 * `protegidos` são os códigos da escrita em curso: quem acabou de pedir um lote precisa receber
 * esse lote de volta. Sem isso, um lote maior que o teto descartaria a si mesmo e o chamador
 * ficaria sem os produtos que pediu.
 */
const evict = (protegidos?: Set<number>) => {
  if (store.size <= MAX_PRODUTOS) return;
  for (const cod of store.keys()) {
    if (store.size <= MAX_PRODUTOS) break;
    if (pinned.has(cod)) continue;
    if (protegidos?.has(cod)) continue;
    store.delete(cod);
  }
};

/** Snapshot só é reconstruído em escrita — `useSyncExternalStore` exige referência estável. */
const publish = () => {
  snapshot = Object.fromEntries(store);
  emit();
};

export const subscribeProductDictionary = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const readProductDictionary = (): Record<number, ProductProps> => snapshot;

/** Produto já resolvido — em cache ou já conhecido como inexistente no escopo. */
export const isProductResolved = (cod: number): boolean => store.has(cod) || misses.has(cod);

export const getCachedProduct = (cod: number): ProductProps | undefined => store.get(cod);

export const putProductsInDictionary = (produtos: ProductProps[], { pin = false } = {}): void => {
  if (!produtos.length) return;

  let changed = false;
  const doLote = new Set<number>();
  for (const produto of produtos) {
    const cod = Number(produto?.COD_PRODUTO);
    if (!Number.isFinite(cod)) continue;
    // Só republica se algo mudou de fato: o snapshot é dependência de efeitos que chamam de
    // volta este store, e publicar à toa fecharia um laço.
    if (store.get(cod) !== produto) changed = true;
    store.set(cod, produto);
    doLote.add(cod);
    if (misses.delete(cod)) changed = true;
    if (pin) pinned.add(cod);
  }

  if (!changed) return;
  evict(doLote);
  publish();
};

/** Protege códigos do descarte sem tocar no snapshot — fixar não muda o conteúdo. */
export const pinProducts = (codigos: number[]): void => {
  for (const cod of codigos) {
    if (Number.isFinite(cod)) pinned.add(cod);
  }
};

/**
 * Marca códigos que foram abertos e **não viraram produto** (restritos para o cliente, ou sem
 * embalagem no escopo), para não tentar de novo a cada página.
 *
 * Passe só os que a hidratação de fato não devolveu. Deduzir isso de "não está no cache" é
 * errado: um produto pode ter sido aberto com sucesso e descartado pelo teto logo em seguida, e
 * marcá-lo como inexistente o apagaria da lista para sempre.
 */
export const markProductMisses = (codigos: number[]): void => {
  for (const cod of codigos) {
    if (Number.isFinite(cod) && !store.has(cod)) misses.add(cod);
  }
};

/** Solta a fixação de códigos que saíram do carrinho — voltam a ser descartáveis. */
export const unpinProducts = (codigos: number[]): void => {
  for (const cod of codigos) pinned.delete(cod);
};

/** Troca de cliente/loja invalida tudo: preço e embalagem são por escopo. */
export const resetProductDictionaryForScope = (signature: string): void => {
  if (signature === scopeSignature) return;
  scopeSignature = signature;
  clearProductDictionary();
};

/** Chamar após um sync — os produtos gravados mudaram. */
export const clearProductDictionary = (): void => {
  store.clear();
  pinned.clear();
  misses.clear();
  publish();
};

/** Só para teste e diagnóstico. */
export const productDictionaryStats = () => ({
  size: store.size,
  pinned: pinned.size,
  misses: misses.size,
  max: MAX_PRODUTOS,
});
